/* eslint-disable no-unused-vars */
import React, {useCallback, useEffect, useState} from 'react';

import {
  faFile,
  faFileAlt,
  faFileArchive,
  faFileAudio,
  faFileCode,
  faFileExcel,
  faFileImage,
  faFilePdf,
  faFilePowerpoint,
  faFileVideo,
  faFileWord,
} from '@fortawesome/free-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {CircularProgress, Typography} from '@material-ui/core';
import Box from '@material-ui/core/Box';
import {makeStyles, withStyles} from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import ArrowDownwardRoundedIcon from '@material-ui/icons/ArrowDownwardRounded';
import CheckCircleRoundedIcon from '@material-ui/icons/CheckCircleRounded';
import ErrorIcon from '@material-ui/icons/Error';

import {FILE_TYPE, getFileTypeCategory} from '../../api/fileUtil';
import {saveAsFile} from '../../api/msgUtil';
import AttachmentDownloader from '../AttachmentDownloader/AttachmentDownloader';
import BrandedButton from '../BrandedButton/BrandedButton';

import styles, {
  attachmentButtonStyles,
  attachmentCaptionContainerStyles,
  attachmentInfoContainerStyles,
  downloadDecoratorStyles,
  downloadedDecoratorStyles,
  spinnerStyles,
} from './style';

const ATT_STATUS_NOT_DOWNLOADED = 'NOT_DOWNLOADED';
const ATT_STATUS_DOWNLOADING = 'DOWNLOADING';
const ATT_STATUS_DOWNLOADED = 'DOWNLOADED';
const ATT_STATUS_ERROR = 'ERROR';

const useStyles = makeStyles(styles);

const AttachmentButton = withStyles(attachmentButtonStyles)(BrandedButton);
const AttachmentCaptionContainer = withStyles(attachmentCaptionContainerStyles)(
    Box,
);
const AttachmentInfoContainer = withStyles(attachmentInfoContainerStyles)(Box);

const useStylesSpinner = makeStyles(spinnerStyles);

/**
 * Fixed width loading spinner to match Zoom Client
 * @param {Object} props
 * @return {ReactElement}
 */
function LoadingIcon(props) {
  const classes = useStylesSpinner();

  const {className, style, ...childProps} = props;
  return (
    <div className={className} style={style}>
      <div className={classes.root}>
        <CircularProgress
          variant="indeterminate"
          disableShrink
          className={classes.top}
          classes={{
            circle: classes.circle,
          }}
          size={16}
          thickness={7}
          {...childProps}
        />
      </div>
    </div>
  );
}

const DownloadedIconDecorator = withStyles(downloadedDecoratorStyles)(
    CheckCircleRoundedIcon,
);
const DownloadingIconDecorator = withStyles(downloadDecoratorStyles)(
    LoadingIcon,
);
const DownloadIconDecorator = withStyles(downloadDecoratorStyles)(
    ArrowDownwardRoundedIcon,
);

const ErrorDownloadingIconDecorator = withStyles(downloadDecoratorStyles)(
    ErrorIcon,
);

// Icon decorator for main attachment icon depicting
// the attachment's download status
const getDecorator = (downloadStatus) => {
  switch (downloadStatus) {
    case ATT_STATUS_DOWNLOADED:
      return DownloadedIconDecorator;
    case ATT_STATUS_DOWNLOADING:
      return DownloadingIconDecorator;
    case ATT_STATUS_ERROR:
      return ErrorDownloadingIconDecorator;
    case ATT_STATUS_NOT_DOWNLOADED:
    default:
      return DownloadIconDecorator;
  }
};

/**
 * Returns the appropriate icon with decorator for the given filename
 * and attachment download status
 * @param {string} filename
 * @param {string} downloadStatus
 * @param {boolean} showDecorator
 * @return {ReactElement}
 */
const getAttIcon = (filename, downloadStatus, showDecorator) => {
  let AttIcon = faFile;
  const fileType = getFileTypeCategory(filename);
  switch (fileType) {
    case FILE_TYPE.SIMPLE_TEXT:
      AttIcon = faFileAlt;
      break;
    case FILE_TYPE.WORD:
      AttIcon = faFileWord;
      break;
    case FILE_TYPE.PPT:
      AttIcon = faFilePowerpoint;
      break;
    case FILE_TYPE.PDF:
      AttIcon = faFilePdf;
      break;
    case FILE_TYPE.IMAGE:
      AttIcon = faFileImage;
      break;
    case FILE_TYPE.ZIP:
      AttIcon = faFileArchive;
      break;
    case FILE_TYPE.EXCEL:
      AttIcon = faFileExcel;
      break;
    case FILE_TYPE.CODE:
      AttIcon = faFileCode;
      break;
    case FILE_TYPE.AUDIO:
      AttIcon = faFileAudio;
      break;
    case FILE_TYPE.VIDEO:
      AttIcon = faFileVideo;
      break;
    default:
      break;
  }

  const DecoratorIcon = getDecorator(downloadStatus);

  return (
    <>
      <FontAwesomeIcon icon={AttIcon} fontSize="large"/>
      <DecoratorIcon
        className="startIconDecorator startIconDecoratorContainer"
        fontSize="small"
        aria-label={downloadStatus.toLocaleLowerCase()}
        style={{
          visibility: showDecorator ? 'unset' : 'hidden',
        }}
      />
    </>
  );
};

const getDisplaySize = (byteSize) => {
  const base = 1000;
  let scale = 0;
  let remainder = byteSize;
  while (remainder > base) {
    remainder = remainder / base;
    scale += 1;
  }
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  return `${Math.round(remainder)} ${units[scale]}`;
};

/**
 * Attachment Component
 * @param {Object} props
 * @param {function} props.addToFileList
 * @param {string} props.attachment.filename
 * @param {string} props.attachment.mimeType
 * @param {string} props.attachment.id
 * @param {number} props.attachment.size
 * @param {DecrypterSession} props.decrypter
 * @param {boolean} props.downloadAllAtt
 * @param {string} props.searchStr the querystring identifying a message
 * @return {ReactElement}
 */
function Attachment(props) {
  const {
    addToFileList,
    attachment,
    decrypter,
    downloadAllAtt,
    searchStr,
  } = props;

  const classes = useStyles();

  const [attachmentData, setAttachmentData] = useState(null);
  const [downloadStatus, setDownloadStatus] = useState(
      ATT_STATUS_NOT_DOWNLOADED,
  );

  useEffect(() => {
    if (downloadAllAtt) {
      if (attachmentData) {
        addToFileList({
          filename: attachment.filename,
          data: attachmentData},
        );
      } else {
        handleDownload();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadAllAtt]);

  const [isFocused, setIsFocused] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  const showingDownloader = downloadStatus === ATT_STATUS_DOWNLOADING;

  const showDecorator =
    isHovering ||
    isFocused ||
    showingDownloader ||
    downloadStatus === ATT_STATUS_DOWNLOADED ||
    downloadStatus === ATT_STATUS_ERROR;

  const handleDownload = async () => {
    setDownloadStatus(ATT_STATUS_DOWNLOADING);
  };

  const handleSave = useCallback(async () => {
    try {
      saveAsFile(attachment.filename, attachmentData, attachment.mimeType);
    } catch (error) {
      console.log('error saving attachment=', error);
    }
  }, [attachment.filename, attachment.mimeType, attachmentData]);

  const handleClick = async (e) => {
    e.preventDefault();
    if (showingDownloader) {
      return;
    }
    if (downloadStatus === ATT_STATUS_DOWNLOADED) {
      handleSave();
    } else {
      handleDownload();
    }
  };

  const onDownloadCancel = useCallback(() => {
    setDownloadStatus(ATT_STATUS_NOT_DOWNLOADED);
  }, []);

  const onError = useCallback(() => {
    setDownloadStatus(ATT_STATUS_ERROR);
    if (downloadAllAtt) {
      addToFileList({filename: attachment.filename, data: ''});
    }
  }, [addToFileList, attachment.filename, downloadAllAtt]);


  const onDownloadComplete = useCallback((attData) => {
    setDownloadStatus(ATT_STATUS_DOWNLOADED);
    setAttachmentData(attData);
  }, []);

  useEffect(() => {
    if (attachmentData) {
      if (downloadAllAtt) {
        addToFileList({filename: attachment.filename, data: attachmentData});
      } else {
        handleSave();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachmentData]);

  return (
    <AttachmentButton
      component="div"
      color="primary"
      data-testid={`attachment-${attachment.id}`}
      variant="outlined"
      size="large"
      startIcon={getAttIcon(attachment.filename, downloadStatus, showDecorator)}
      onClick={handleClick}
      onFocus={(e) => setIsFocused(true)}
      onMouseDown={(e) => e.preventDefault()}
      onBlur={(e) => setIsFocused(false)}
      onMouseEnter={(e) => setIsHovering(true)}
      onMouseLeave={(e) => setIsHovering(false)}
      disableRipple
      aria-label={`Attachment download button for ${attachment.filename}
      of file type ${attachment.mimeType}`}
    >
      <Tooltip
        title={attachment.filename?.length > 20 ? attachment.filename : ''}>
        <AttachmentInfoContainer>
          <div className={classes.labelText}>
            <b>{attachment.filename}</b>
          </div>

          <AttachmentCaptionContainer>
            <Typography variant="caption" noWrap>
              {getDisplaySize(attachment.size)}
            </Typography>
            {downloadStatus === ATT_STATUS_DOWNLOADING && (
              <AttachmentDownloader
                attachment={attachment}
                searchStr={searchStr}
                decrypter={decrypter}
                onCancel={onDownloadCancel}
                onComplete={onDownloadComplete}
                onError={onError}
              />
            )}
          </AttachmentCaptionContainer>
        </AttachmentInfoContainer>
      </Tooltip>
    </AttachmentButton>

  );
}

export default Attachment;
export const exportedForTesting = {
  LoadingIcon,
  getDecorator,
  getAttIcon,
  getDisplaySize,
};
